/**
 * SPIN TENET — Visualization Runtime Contracts
 *
 * Defines typed interfaces for visual layers, camera, residency, probes,
 * measurements, and hardware capabilities.
 */

export type GpuBackendType = 'webgpu' | 'webgl2' | 'native_vulkan' | 'native_metal' | 'native_dx12';

export interface GpuCapabilities {
  backend: GpuBackendType;
  computeShaders: boolean;
  storageBuffers: boolean;
  texture3D: boolean;
  maxTextureDimension3D: number;
  maxBufferSizeMb: number;
  adapterInfo: {
    vendor: string;
    architecture: string;
    device: string;
    description: string;
  };
}

export type VisualLayerType =
  | 'volume'
  | 'isosurface'
  | 'vector_glyph'
  | 'streamline'
  | 'urban_mesh'
  | 'terrain'
  | 'probe_overlay';

export interface VisualLayer {
  layerId: string;
  layerType: VisualLayerType;
  visible: boolean;
  fieldId?: string;
  opacity: number;
  colormap: string;
  colorRange?: [number, number];
  wireframe?: boolean;
  config: Record<string, unknown>;
}

export interface CameraState {
  position: [number, number, number];
  target: [number, number, number];
  up: [number, number, number];
  fovY: number;
  near: number;
  far: number;
  projection: 'perspective' | 'orthographic';
}

export type ResidencyState =
  | 'REMOTE'
  | 'CACHED'
  | 'DECODING'
  | 'CPU_RESIDENT'
  | 'GPU_UPLOADING'
  | 'GPU_RESIDENT'
  | 'EVICTING';

export interface ChunkResidency {
  chunkKey: string;
  fieldId: string;
  state: ResidencyState;
  sizeBytes: number;
  lastAccessed: number;
}

export interface ResidencyBudget {
  maxGpuVramMb: number;
  maxRamMb: number;
  maxOpfsMb: number;
  currentGpuVramMb: number;
  currentRamMb: number;
  currentOpfsMb: number;
}

export type ProbeType = 'point' | 'line' | 'plane' | 'box';

export interface ProbeDescriptor {
  probeId: string;
  probeType: ProbeType;
  fieldId: string;
  coordinates: number[][]; // Point: [[x,y,z]], Line: [[x1,y1,z1], [x2,y2,z2]]
  samples?: number;
}

export interface ProbeResult {
  probeId: string;
  fieldId: string;
  timestamp: number;
  values: number[];
  positions: [number, number, number][];
  unit: string;
}

export interface Measurement {
  measurementId: string;
  measurementType: 'distance' | 'flux' | 'area_integral' | 'volume_integral';
  endpoints: [number, number, number][];
  resultValue: number;
  unit: string;
}

export interface SelectionState {
  selectedEntityId?: string;
  selectedFieldId?: string;
  selectedLayerId?: string;
  pickedCoordinate?: [number, number, number];
  pickedValue?: number;
}

/**
 * VIEWPORT PHYSICAL CONSTRUCTION UNIT CONTRACT
 *
 * Establishes the authoritative physical scale and coordinate convention
 * for the SPIN TENET design and construction viewport.
 *
 * SCIENTIFIC BOUNDARY:
 * This contract applies strictly to the DESIGN / CONSTRUCTION viewport coordinate system.
 * It is NOT a CFD computational domain definition, NOT solver mesh resolution,
 * and implies zero discretization or numerical convergence guarantees.
 *
 * Design / Construction Space (1 unit = 1 metre)
 *          ↓
 * Simulation Preparation / Boundary Specification
 *          ↓
 * CFD Computational Domain (OpenLB / OpenFOAM / PALM / waLBerla)
 *          ↓
 * Mesh / Solver Discretization (dx, octree, unstructured)
 */
export const VIEWPORT_CONSTRUCTION_SPACE = {
  /** Base SI physical unit name for viewport construction space */
  unit: 'metre' as const,
  /** Plural designation */
  unitPlural: 'metres' as const,
  /** Standard SI abbreviation */
  unitSymbol: 'm' as const,
  /**
   * Scale factor relating world-space units to SI metres:
   * Exactly 1.0 world-space unit === 1.0 metre.
   */
  worldUnitsPerMetre: 1.0,
  /**
   * Coordinate Reference System convention:
   * Right-handed Cartesian: +X = East/Right (m), +Y = Elevation/Up (m), +Z = Depth/Forward (m)
   */
  crs: 'local_cartesian' as const,
  /** Up-vector direction */
  upAxis: 'Y' as const,
  /** Ground plane definition */
  groundPlane: 'XZ' as const,
  groundElevationY: 0.0,
  /** World Origin in physical metres */
  origin: [0.0, 0.0, 0.0] as const,
  /**
   * Canonical diagnostic construction grid physical configuration
   */
  defaultGrid: {
    extentMetres: 40.0,           // Extends ±40.0 m from origin along X and Z
    totalFootprintMetres: 80.0,    // 80.0 m × 80.0 m total ground footprint
    minorSpacingMetres: 1.0,       // Minor lines spaced every 1.0 m
    majorSpacingMetres: 5.0,       // Major lines spaced every 5.0 m
    originAxisLengthMetres: 6.0    // Primary XYZ reference vectors length = 6.0 m
  }
} as const;

export type ViewportConstructionSpace = typeof VIEWPORT_CONSTRUCTION_SPACE;

/**
 * GroundPoint — Exact world-space intersection on the construction ground plane Y = 0.
 * Coordinates are physical metres in local_cartesian CRS.
 */
export interface GroundPoint {
  x: number;
  y: 0;
  z: number;
}

/**
 * Snapping policy mode in construction space.
 * Future extensions: 'vertex' | 'edge' | 'midpoint' | 'center'.
 */
export type SnapMode = 'off' | 'grid';

/**
 * Construction grid snapping configuration contract.
 */
export interface GridSnapConfig {
  mode: SnapMode;
  spacingMetres: number;
}

/**
 * Explicit Construction Rectangle Minimum Dimension Contract:
 * Exactly 10 ft × 10 ft.
 * 10 feet === 3.048 metres.
 * Applied strictly to construction rectangle commit validation.
 * NOT a CFD mesh or solver discretization resolution.
 */
export const MIN_RECTANGLE_WIDTH_M = 3.048;
export const MIN_RECTANGLE_DEPTH_M = 3.048;

/**
 * Authoritative Canonical Rectangle Construction Geometry Contract:
 * Exists strictly on construction ground plane Y = 0 (metres).
 * Width: dimension along local X axis.
 * Depth: dimension along local Z axis.
 * RotationY: rotation around +Y axis in radians (initial: 0).
 */
export interface RectangleGeometry {
  id: string;
  type: 'rectangle';
  center: GroundPoint;
  width: number;
  depth: number;
  rotationY: number;
  height?: number;
}

/**
 * Circle Construction Geometry Contract:
 * Defined on construction ground plane Y = 0 (metres).
 * Radius: radius in metres (>= 1.0m).
 * Height: positive vertical extrusion in metres if extruded (3D).
 */
export interface CircleGeometry {
  id: string;
  type: 'circle';
  center: GroundPoint;
  radius: number;
  rotationY: number;
  height?: number;
  width?: number;
  depth?: number;
}

/**
 * Spline Construction Geometry Contract:
 * Defined by planar control points on ground plane Y = 0.
 * Points: series of ground points defining the 2D polygon/spline profile.
 * Height: positive vertical extrusion in metres if extruded (3D).
 */
export interface SplineGeometry {
  id: string;
  type: 'spline';
  center: GroundPoint;
  points: GroundPoint[];
  rotationY: number;
  height?: number;
  width?: number;
  depth?: number;
}

/**
 * Authoritative Canonical 3D Building Mass Construction Geometry Contract (GEOMETRY-02):
 * Extruded vertically from a rectangular ground footprint along the positive +Y axis.
 * Base elevation: exactly Y = 0.0 m (construction ground plane).
 * Top elevation: exactly Y = height.
 * Width: dimension along local X axis (metres).
 * Depth: dimension along local Z axis (metres).
 * RotationY: rotation around +Y axis in radians.
 * Height: positive vertical extrusion in metres (> 0).
 */
export interface BuildingMassGeometry {
  id: string;
  type: 'building_mass';
  center: GroundPoint;
  width: number;
  depth: number;
  rotationY: number;
  height: number;
  shapeType?: 'rectangle' | 'circle' | 'spline';
  radius?: number;
  points?: GroundPoint[];
}

export type ConstructionGeometry =
  | RectangleGeometry
  | CircleGeometry
  | SplineGeometry
  | BuildingMassGeometry;

/**
 * Validates whether candidate rectangle dimensions satisfy the project minimum size contract (>= 3.048m).
 */
export function isValidRectangleGeometry(width: number, depth: number): boolean {
  return (
    Number.isFinite(width) &&
    Number.isFinite(depth) &&
    width >= MIN_RECTANGLE_WIDTH_M &&
    depth >= MIN_RECTANGLE_DEPTH_M
  );
}

export const MIN_CIRCLE_RADIUS_M = 1.0;
export function isValidCircleGeometry(radius: number): boolean {
  return Number.isFinite(radius) && radius >= MIN_CIRCLE_RADIUS_M;
}

/**
 * Validates building mass / extrusion height: must be finite and strictly positive (> 0 m).
 * No arbitrary minimum or default is enforced.
 */
export function isValidBuildingMassHeight(height: number): boolean {
  return Number.isFinite(height) && height > 0;
}

export const isValidExtrusionHeight = isValidBuildingMassHeight;

/**
 * VIEWPORT DISPLAY BOUNDS
 * Not the authoritative CFD computational domain.
 * Strictly a visualization framing / cropping boundary.
 */
export interface ViewportDisplayBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
  width: number;
  length: number;
  height: number;
}

/**
 * Calculates temporary viewport display bounds from current study geometry and reference grid:
 * - X display bounds: geometry/grid extent ± 10 m
 * - Z display bounds: geometry/grid extent ± 10 m
 * - Y display bounds: 0 → max(20 m, tallest building height + appropriate visual clearance)
 *
 * NOTE: VIEWPORT DISPLAY BOUNDS — Not the authoritative CFD computational domain.
 */
export function computeViewportDisplayBounds(
  geometries: { center: { x: number; y: number; z: number }; width: number; depth: number; height?: number }[],
  gridExtentMetres: number = VIEWPORT_CONSTRUCTION_SPACE.defaultGrid.extentMetres
): ViewportDisplayBounds {
  let minGeomX: number = -gridExtentMetres;
  let maxGeomX: number = gridExtentMetres;
  let minGeomZ: number = -gridExtentMetres;
  let maxGeomZ: number = gridExtentMetres;
  let tallestBuilding = 0;

  if (geometries && geometries.length > 0) {
    for (const g of geometries) {
      const halfW = g.width / 2.0;
      const halfD = g.depth / 2.0;
      minGeomX = Math.min(minGeomX, g.center.x - halfW);
      maxGeomX = Math.max(maxGeomX, g.center.x + halfW);
      minGeomZ = Math.min(minGeomZ, g.center.z - halfD);
      maxGeomZ = Math.max(maxGeomZ, g.center.z + halfD);
      if (typeof g.height === 'number' && Number.isFinite(g.height)) {
        tallestBuilding = Math.max(tallestBuilding, g.height);
      }
    }
  }

  const visualClearance = 10.0;
  const minX = minGeomX - 10.0;
  const maxX = maxGeomX + 10.0;
  const minZ = minGeomZ - 10.0;
  const maxZ = maxGeomZ + 10.0;
  const minY = 0.0;
  const maxY = Math.max(20.0, tallestBuilding + visualClearance);

  return {
    minX,
    maxX,
    minY,
    maxY,
    minZ,
    maxZ,
    width: maxX - minX,
    length: maxZ - minZ,
    height: maxY - minY
  };
}



